import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe, LogIn, User, GraduationCap, BookOpen, Users, TrendingUp, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import CartDrawer from './CartDrawer';

const Header = () => {
  const { language, toggleLanguage } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const content = {
    en: {
      home: "Home",
      about: "About",
      courses: "Our Courses",
      contact: "Contact Us",
      blogs: "Blogs",
      registration: "Free Assessment",
      login: "Login",
      loginAs: "Login as",
      studentLogin: "Student Portal",
      staffLogin: "Staff",
      admin: "Admin",
      moderator: "Moderator",
      teacher: "Teacher",
      student: "Student",
      affiliate: "Affiliate"
    },
    ar: {
      home: "الرئيسية",
      about: "من نحن",
      courses: "دوراتنا",
      contact: "اتصل بنا",
      blogs: "المدونات",
      registration: "تقييم مجاني",
      login: "تسجيل الدخول",
      loginAs: "تسجيل الدخول كـ",
      studentLogin: "بوابة الطالب",
      staffLogin: "الموظفين",
      admin: "مدير",
      moderator: "مشرف",
      teacher: "معلم",
      student: "طالب",
      affiliate: "شريك"
    }
  };

  const t = content[language];

  // Staff login options (Admin, Moderator, Teacher, Affiliate) - shown in dropdown
  const staffLoginOptions = [
    { role: 'admin', label: t.admin, icon: User, color: 'text-red-600 bg-red-50 hover:bg-red-100', path: '/login?role=admin' },
    { role: 'moderator', label: t.moderator, icon: Users, color: 'text-purple-600 bg-purple-50 hover:bg-purple-100', path: '/login?role=moderator' },
    { role: 'teacher', label: t.teacher, icon: GraduationCap, color: 'text-blue-600 bg-blue-50 hover:bg-blue-100', path: '/login?role=teacher' },
    { role: 'affiliate', label: t.affiliate, icon: TrendingUp, color: 'text-amber-600 bg-amber-50 hover:bg-amber-100', path: '/login?role=affiliate' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#home" className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_english-academy-lms/artifacts/fhrsr91w_profile_picture.png"
              alt="Canada English Life Academy"
              className="h-12 w-auto object-contain"
            />
            <div>
              <h1 className="text-lg font-bold text-gray-900">Canada English Life</h1>
              <p className="text-xs text-gray-600">{language === 'en' ? 'Academy Since 2017' : 'أكاديمية منذ 2017'}</p>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <a href="#home" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">{t.home}</a>
            <a href="#about" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">{t.about}</a>
            <a href="#courses" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">{t.courses}</a>
            <a href="#contact" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">{t.contact}</a>
            <a href="#blogs" className="text-gray-700 hover:text-teal-600 transition-colors font-medium">{t.blogs}</a>
          </nav>

          {/* CTA and Language Toggle */}
          <div className="flex items-center space-x-3">
            <CartDrawer />
            
            {/* Prominent Student Login Button */}
            <Link to="/login?role=student" className="hidden md:block">
              <Button
                className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-5 py-2 shadow-md hover:shadow-lg transition-all"
                data-testid="student-login-btn"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                {t.studentLogin}
              </Button>
            </Link>
            
            {/* Staff Login Dropdown (smaller, less prominent) */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="hidden md:flex items-center space-x-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-all"
                  data-testid="staff-login-dropdown"
                >
                  <LogIn className="w-4 h-4" />
                  <span className="text-sm">{t.staffLogin}</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-52 p-2" align="end">
                <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-1">{t.loginAs}</div>
                {staffLoginOptions.map((option) => (
                  <Link
                    key={option.role}
                    to={option.path}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${option.color}`}
                  >
                    <option.icon className="w-4 h-4" />
                    <span className="font-medium text-sm">{option.label}</span>
                  </Link>
                ))}
              </PopoverContent>
            </Popover>
            
            <Button
              onClick={toggleLanguage}
              variant="outline"
              size="sm"
              className="hidden md:flex items-center space-x-2 hover:bg-teal-50 hover:border-teal-500 transition-all"
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'en' ? 'العربية' : 'English'}</span>
            </Button>
            
            <a 
              href="https://link.content-presence.com/widget/bookings/gaby-meeting"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="hidden md:inline-flex bg-teal-600 hover:bg-teal-700 text-white transition-colors">
                {t.registration}
              </Button>
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-700 hover:text-teal-600 transition-colors"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t pt-4">
            <nav className="flex flex-col space-y-3">
              <a href="#home" className="text-gray-700 hover:text-teal-600 transition-colors font-medium py-2">{t.home}</a>
              <a href="#about" className="text-gray-700 hover:text-teal-600 transition-colors font-medium py-2">{t.about}</a>
              <a href="#courses" className="text-gray-700 hover:text-teal-600 transition-colors font-medium py-2">{t.courses}</a>
              <a href="#contact" className="text-gray-700 hover:text-teal-600 transition-colors font-medium py-2">{t.contact}</a>
              <a href="#blogs" className="text-gray-700 hover:text-teal-600 transition-colors font-medium py-2">{t.blogs}</a>
              <div className="pt-4 space-y-3">
                {/* Prominent Student Login Button (Mobile) */}
                <Link 
                  to="/login?role=student"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full"
                >
                  <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 text-lg shadow-md">
                    <BookOpen className="w-5 h-5 mr-2" />
                    {t.studentLogin}
                  </Button>
                </Link>
                
                {/* Staff Login Options (Mobile) */}
                <div className="text-xs font-medium text-gray-500 px-2 py-1">{t.staffLogin}</div>
                <div className="grid grid-cols-2 gap-2">
                  {staffLoginOptions.map((option) => (
                    <Link
                      key={option.role}
                      to={option.path}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${option.color}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <option.icon className="w-4 h-4" />
                      <span className="font-medium">{option.label}</span>
                    </Link>
                  ))}
                </div>
                
                <Button
                  onClick={toggleLanguage}
                  variant="outline"
                  className="w-full flex items-center justify-center space-x-2 mt-2"
                >
                  <Globe className="w-4 h-4" />
                  <span>{language === 'en' ? 'العربية' : 'English'}</span>
                </Button>
                <a 
                  href="https://link.content-presence.com/widget/bookings/gaby-meeting"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full"
                >
                  <Button className="w-full bg-gray-800 hover:bg-gray-900 text-white">
                    {t.registration}
                  </Button>
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
